import { User, Edit, Trash2, Loader2 } from 'lucide-react'

interface PersonaCardProps {
  persona: {
    id: string
    name: string
    role: string
    description: string | null
    expertise_areas: string[] | null
    platforms: string[] | null
    voice_config: Record<string, unknown> | null
    avatar_url: string | null
  }
  onEdit: () => void
  onDelete: () => void
  isDeleteDisabled?: boolean
}

export function PersonaCard({ persona, onEdit, onDelete, isDeleteDisabled = false }: PersonaCardProps) {
  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      CEO: 'bg-purple-100 text-purple-700',
      CTO: 'bg-blue-100 text-blue-700',
      Marketing: 'bg-pink-100 text-pink-700',
      Brand: 'bg-emerald-100 text-emerald-700',
      Guest: 'bg-amber-100 text-amber-700',
    }
    return colors[role] || 'bg-slate-100 text-slate-700'
  }

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {persona.avatar_url && persona.avatar_url.trim() !== '' ? (
            <img src={persona.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <User className="w-6 h-6 text-emerald-700" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-slate-800">{persona.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${getRoleColor(persona.role)}`}>
              {persona.role}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-emerald-600 transition-colors"
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            disabled={isDeleteDisabled}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title="Eliminar"
          >
            {isDeleteDisabled ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {persona.description && (
        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
          {persona.description}
        </p>
      )}

      {persona.expertise_areas && persona.expertise_areas.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-1">Expertise:</p>
          <div className="flex flex-wrap gap-1">
            {persona.expertise_areas.slice(0, 3).map((area, index) => (
              <span key={index} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                {area}
              </span>
            ))}
            {persona.expertise_areas.length > 3 && (
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                +{persona.expertise_areas.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {persona.platforms && persona.platforms.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1">Plataformas:</p>
          <div className="flex flex-wrap gap-1">
            {persona.platforms.map((platform, index) => (
              <span key={index} className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">
                {platform}
              </span>
            ))}
          </div>
        </div>
      )}

      {persona.voice_config && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            Voz: {(persona.voice_config as any).tone || 'N/A'} • {(persona.voice_config as any).style || 'N/A'}
          </p>
        </div>
      )}
    </div>
  )
}
